import { useMemo } from 'react';
import { useQuery, QueryResult } from '@apollo/client';

import { useUserSegments } from 'hooks/use-user-segments';
import useUI from 'hooks/use-ui';
import { getChannelFromVariant } from 'src/utils/collection-channel';
import { GetMenuSectionsDocument, GetMenuSectionsQuery } from 'src/gql/graphql';

type UseMenuSectionsOptions = {
  dispensaryId?: string;
  skip?: boolean;
};

type QueryHookReturn = QueryResult<GetMenuSectionsQuery>;

type UseMenuSectionsReturn = {
  // undefined until resolved, then an array (possibly empty).
  menuSections: NonNullable<NonNullable<QueryHookReturn['data']>['getMenuSections']> | undefined;
  loading: boolean;
  error: QueryHookReturn['error'];
  refetch: QueryHookReturn['refetch'];
};

export default function useMenuSections({ dispensaryId, skip = false }: UseMenuSectionsOptions): UseMenuSectionsReturn {
  const { segments: userSegments, loading: segmentsLoading } = useUserSegments({ dispensaryId });
  const sortedSegments = useMemo(() => [...userSegments].sort(), [userSegments]);
  const userSegmentIds = sortedSegments.length > 0 ? sortedSegments : undefined;
  // Unflagged here; the API gates it.
  const UI = useUI();
  const channel = getChannelFromVariant(UI?.variant);

  const isSkipped = skip || !dispensaryId || segmentsLoading;

  const { data, loading, error, refetch } = useQuery(GetMenuSectionsDocument, {
    variables: {
      dispensaryId: dispensaryId ?? '',
      userSegmentIds,
      channel,
    },
    skip: isSkipped,
    fetchPolicy: 'cache-and-network',
  });

  // Use cached data even while `loading` (cache-and-network revalidation).
  const resolvedMenuSections = data?.getMenuSections;
  const hasResolved = !isSkipped && !error && resolvedMenuSections !== undefined;

  return {
    menuSections: hasResolved ? resolvedMenuSections ?? [] : undefined,
    loading: skip ? false : loading || segmentsLoading,
    error,
    refetch,
  };
}
